import { useCallback, useEffect, useState } from 'react';

import { ELocalStorageKeys } from '../constants/enums';
import { localStorageUtil } from '../utils/localStorageUtil';

/**
 * Custom hook that syncs with localStorage and automatically re-renders when localStorage changes
 * @param key - The localStorage key to watch
 * @param initialValue - Initial value if key doesn't exist
 * @returns [value, setValue] tuple similar to useState
 */

export function useLocalStorage<T>(key: ELocalStorageKeys, initialValue: T) {
    // Get initial value from localStorage or use provided initial value
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            const item = localStorageUtil.getAny<T>(key);
            return item !== null ? item : initialValue;
        } catch (error) {
            console.error(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    });

    // Custom setter that updates both state and localStorage
    const setValue = useCallback((value: T | ((val: T) => T)) => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;

            localStorageUtil.set(key, valueToStore);
            setStoredValue(valueToStore);

            window.dispatchEvent(new CustomEvent('localStorage-change', {
                detail: { key: key as string, value: valueToStore }
            }));
        } catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error);
        }
    }, [key, storedValue]);

    // Listen for changes from other tabs/components
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === key as string && e.newValue !== null) {
                try {
                    const newValue = JSON.parse(e.newValue);
                    setStoredValue(newValue);
                } catch (error) {
                    console.error(`Error parsing localStorage value for key "${key}":`, error);
                }
            }
        };

        // Listen for custom localStorage events (same tab)
        const handleCustomStorageChange = (e: CustomEvent) => {
            if (e.detail.key === key as string) {
                setStoredValue(e.detail.value);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('localStorage-change', handleCustomStorageChange as EventListener);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('localStorage-change', handleCustomStorageChange as EventListener);
        };
    }, [key]);

    return [storedValue, setValue] as const;
}
